import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
    useGetPatientLabQuery,
    useUpdatePatientLabMutation,
} from "store";
import {
    Box,
    Spinner,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Checkbox,
    Button,
    Input,
    Text,
    Textarea,
    Grid,
    GridItem,
    Link as ChakraLink,
} from "@chakra-ui/react";

const LabUpdate = () => {
    const { patientId, labId } = useParams();
    console.log("Params:", { patientId, labId });
    const navigate = useNavigate();

    // Fetch the existing prescription data
    const {
        data: lab,
        error,
        isLoading,
    } = useGetPatientLabQuery({
        patientId,
        labId,
    });

    // Mutation hook for updating the prescription
    const [
        updatePatientLab,
        { isLoading: isUpdating, error: updateError },
    ] = useUpdatePatientLabMutation();

    // Initialize the form with useForm
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm();

    React.useEffect(() => {
        setValue("patient", patientId);
    }, [patientId, setValue]);

    const capitalizeFirstLetter = (str) => {
        if (!str) return str; // Handle empty string case
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // Populate the form with existing prescription data
    useEffect(() => {
        if (lab) {
            reset({
                specimen: lab.specimen,
                data: lab.data,
                type: lab.type,
            });
        }
    }, [lab, reset]);

    // Handle form submission
    const onSubmit = async (data) => {
        try {
            await updatePatientLab({
                patientId,
                labId,
                ...data,
            }).unwrap();
            navigate(`/patients/${patientId}/labs`);
        } catch (error) {
            console.error("Error updating lab:", error);
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <>
            <Box mt={20} p={4}>
                <Card
                    boxShadow="md"
                    borderRadius="2xl"
                    overflow="hidden"
                    p={4}
                    border="1px solid"
                    borderColor="gray.300"
                >
                    <CardHeader borderBottom="1px solid" borderColor="blue.200">
                        <Box textAlign="center" mb={4}>
                            <Text fontSize="2xl" fontWeight="bold">
                                Update Lab
                            </Text>
                        </Box>
                    </CardHeader>

                    <CardBody>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            
                            {/* Type */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl isInvalid={errors.type}>
                                        <FormLabel>Type</FormLabel>
                                        <Input
                                            type="text"
                                            {...register("type", {
                                                minLength: {
                                                    value: 3,
                                                    message:
                                                        "Type should be at least 3 characters long",
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message:
                                                        "Type should not exceed 500 characters",
                                                },
                                                onChange: (e) => {
                                                    const capitalizedValue = capitalizeFirstLetter(
                                                        e.target.value
                                                    );
                                                    setValue(
                                                        "type",
                                                        capitalizedValue,
                                                        { shouldValidate: true }
                                                    );
                                                },
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.type &&
                                                errors.type.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>
                            
                            {/* Specimen */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl isInvalid={errors.specimen}>
                                        <FormLabel>Specimen</FormLabel>
                                        <Input
                                            type="text"
                                            {...register("specimen", {
                                                minLength: {
                                                    value: 2,
                                                    message:
                                                        "Specimen should be at least 2 characters long",
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message:
                                                        "Specimen should not exceed 500 characters",
                                                },
                                                onChange: (e) => {
                                                    const capitalizedValue = capitalizeFirstLetter(
                                                        e.target.value
                                                    );
                                                    setValue(
                                                        "specimen",
                                                        capitalizedValue,
                                                        { shouldValidate: true }
                                                    );
                                                },
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.specimen &&
                                                errors.specimen.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            {/* Data */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl isInvalid={errors.data}>
                                        <FormLabel>Data</FormLabel>
                                        <Textarea
                                            {...register("data", {
                                                minLength: {
                                                    value: 10,
                                                    message:
                                                        "Data should be at least 10 characters long",
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message:
                                                        "Data should not exceed 500 characters",
                                                },
                                                onChange: (e) => {
                                                    const capitalizedValue = capitalizeFirstLetter(
                                                        e.target.value
                                                    );
                                                    setValue(
                                                        "data",
                                                        capitalizedValue,
                                                        { shouldValidate: true }
                                                    );
                                                },
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.data &&
                                                errors.data.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            <Grid
                                templateColumns="repeat(2, 1fr)"
                                gap={4}
                                width="100%"
                            >
                                <GridItem>
                                    <ChakraLink
                                        as={Link}
                                        to={`/patients/${patientId}/labs/${labId}/details`}
                                        color="blue.500"
                                    >
                                        Back to Details
                                    </ChakraLink>
                                </GridItem>
                                <GridItem textAlign="right">
                                    <Button
                                        colorScheme="blue"
                                        isLoading={isUpdating}
                                        loadingText="Loading..."
                                        spinner={<Spinner />}
                                        type="submit"
                                    >
                                        Update
                                    </Button>
                                </GridItem>
                            </Grid>

                            {updateError && (
                                <div>Error: {updateError.message}</div>
                            )}
                        </form>
                    </CardBody>
                </Card>
            </Box>
        </>
    );
};

export default LabUpdate;
